import { Children, isValidElement, useMemo, useState, type ReactElement, type ReactNode } from 'react';

export type ReleaseLevel = 'major' | 'minor' | 'patch';

export interface ReleaseProps {
  /** Semver, e.g. "3.14.0". The level (major/minor/patch) is derived from it. */
  version: string;
  title: string;
  children: ReactNode;
}

/**
 * A single release. This is a marker component: `ChangelogTimeline` reads the
 * props off its children and renders the body itself, so a `<Release>` is never
 * mounted on its own inside the timeline.
 */
export function Release({ children }: ReleaseProps) {
  return <>{children}</>;
}

const GROUP_TONES: readonly { match: RegExp; dot: string; text: string }[] = [
  { match: /^parent/i, dot: 'bg-emerald-500', text: 'text-emerald-700' },
  { match: /^teacher/i, dot: 'bg-amber-500', text: 'text-amber-700' },
  { match: /^admin/i, dot: 'bg-blue-500', text: 'text-blue-700' },
  { match: /^system/i, dot: 'bg-violet-500', text: 'text-violet-700' },
];
const DEFAULT_TONE = { dot: 'bg-gray-400', text: 'text-gray-600' };

function toneFor(title: string) {
  return GROUP_TONES.find((t) => t.match.test(title)) ?? DEFAULT_TONE;
}

/** One audience section within a release ("Admin functionality", …). Children are `<li>` items. */
export function Group({ title, children }: { title: string; children: ReactNode }) {
  const tone = toneFor(title);
  return (
    <div>
      <h4 className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider">
        <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} aria-hidden="true" />
        <span className={tone.text}>{title}</span>
      </h4>
      <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-gray-700 marker:text-gray-300">
        {children}
      </ul>
    </div>
  );
}

interface Entry {
  version: string;
  title: string;
  level: ReleaseLevel;
  major: number;
  minor: number;
  patch: number;
  body: ReactNode;
}

interface Line {
  key: string;
  label: string;
  head: Entry | null;
  patches: Entry[];
}

interface Series {
  major: number;
  lines: Line[];
  counts: Record<ReleaseLevel, number>;
  total: number;
}

const LEVEL_CHIP: Record<ReleaseLevel, string> = {
  major: 'bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-sm',
  minor: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200',
  patch: 'bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200',
};

const LEVEL_TAG: Record<ReleaseLevel, string> = {
  major: 'text-violet-700 bg-violet-50 ring-violet-200',
  minor: 'text-blue-700 bg-blue-50 ring-blue-200',
  patch: 'text-gray-500 bg-gray-50 ring-gray-200',
};

const FILTERS: readonly (ReleaseLevel | 'all')[] = ['all', 'major', 'minor', 'patch'];

/** Body styling is applied from here so entries stay plain `<Group>`/`<li>` markup. */
const BODY_CLASS =
  'space-y-4 [&_code]:rounded [&_code]:bg-white [&_code]:px-1 [&_code]:py-px [&_code]:font-mono ' +
  '[&_code]:text-[12px] [&_code]:text-gray-700 [&_code]:ring-1 [&_code]:ring-gray-200 [&_strong]:font-semibold [&_strong]:text-gray-900';

function levelOf(minor: number, patch: number): ReleaseLevel {
  if (patch > 0) return 'patch';
  if (minor > 0) return 'minor';
  return 'major';
}

function Chevron({ open, className = '' }: { open: boolean; className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
      className={`h-4 w-4 shrink-0 transition-transform duration-150 ${open ? 'rotate-90' : ''} ${className}`}
    >
      <path
        fillRule="evenodd"
        d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ReleaseBody({ entry }: { entry: Entry }) {
  return <div className={BODY_CLASS}>{entry.body}</div>;
}

export function ChangelogTimeline({ children }: { children: ReactNode }) {
  const entries = useMemo<Entry[]>(() => {
    const parsed = Children.toArray(children)
      .filter((child): child is ReactElement<ReleaseProps> => isValidElement(child) && child.type === Release)
      .map((child) => {
        const [major = 0, minor = 0, patch = 0] = child.props.version.split('.').map(Number);
        return {
          version: child.props.version,
          title: child.props.title,
          level: levelOf(minor, patch),
          major,
          minor,
          patch,
          body: child.props.children,
        };
      });
    return parsed.sort((a, b) => b.major - a.major || b.minor - a.minor || a.patch - b.patch);
  }, [children]);

  const latest = useMemo(
    () => entries.reduce<Entry | null>((best, e) =>
      !best || e.major > best.major || (e.major === best.major && (e.minor > best.minor || (e.minor === best.minor && e.patch > best.patch)))
        ? e
        : best,
    null),
    [entries],
  );

  const [query, setQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<ReleaseLevel | 'all'>('all');
  const [openVersions, setOpenVersions] = useState<ReadonlySet<string>>(() => new Set(latest ? [latest.version] : []));
  const [closedSeries, setClosedSeries] = useState<ReadonlySet<number>>(() => new Set());

  const series = useMemo<Series[]>(() => {
    const q = query.trim().toLowerCase();
    const matches = (e: Entry) =>
      (levelFilter === 'all' || e.level === levelFilter) &&
      (q === '' || e.version.includes(q) || e.title.toLowerCase().includes(q));

    const bySeries = new Map<number, Map<string, Line>>();
    const counts = new Map<number, Record<ReleaseLevel, number>>();

    for (const entry of entries) {
      if (!matches(entry)) continue;
      let lines = bySeries.get(entry.major);
      if (!lines) {
        lines = new Map();
        bySeries.set(entry.major, lines);
        counts.set(entry.major, { major: 0, minor: 0, patch: 0 });
      }
      const key = `${entry.major}.${entry.minor}`;
      let line = lines.get(key);
      if (!line) {
        line = { key, label: `v${key}.x`, head: null, patches: [] };
        lines.set(key, line);
      }
      if (entry.patch === 0) line.head = entry;
      else line.patches.push(entry);
      const c = counts.get(entry.major);
      if (c) c[entry.level] += 1;
    }

    return [...bySeries.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([major, lines]) => {
        const seriesCounts = counts.get(major) ?? { major: 0, minor: 0, patch: 0 };
        return {
          major,
          lines: [...lines.values()],
          counts: seriesCounts,
          total: seriesCounts.major + seriesCounts.minor + seriesCounts.patch,
        };
      });
  }, [entries, query, levelFilter]);

  const visibleVersions = useMemo(
    () => series.flatMap((s) => s.lines.flatMap((l) => [...(l.head ? [l.head.version] : []), ...l.patches.map((p) => p.version)])),
    [series],
  );
  const allExpanded = visibleVersions.length > 0 && visibleVersions.every((v) => openVersions.has(v));
  const resultCount = visibleVersions.length;

  const toggleVersion = (version: string) =>
    setOpenVersions((prev) => {
      const next = new Set(prev);
      if (!next.delete(version)) next.add(version);
      return next;
    });

  const toggleSeries = (major: number) =>
    setClosedSeries((prev) => {
      const next = new Set(prev);
      if (!next.delete(major)) next.add(major);
      return next;
    });

  const toggleAll = () => setOpenVersions(allExpanded ? new Set() : new Set(visibleVersions));

  return (
    <div className="flex flex-col">
      {/* Toolbar */}
      <div className="sticky top-0 z-20 border-b border-gray-200 bg-white/90 px-4 py-3 backdrop-blur sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[10rem] flex-1">
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400">
              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 3.44 9.79l3.13 3.14a.75.75 0 1 0 1.06-1.06l-3.13-3.14A5.5 5.5 0 0 0 9 3.5ZM5 9a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z" clipRule="evenodd" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search version or title…"
              aria-label="Search releases"
              className="w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center rounded-lg bg-gray-100 p-0.5">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setLevelFilter(f)}
                aria-pressed={levelFilter === f}
                className={`rounded-md px-2.5 py-1 text-xs font-medium capitalize transition-colors ${
                  levelFilter === f ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={toggleAll}
            className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500 tabular-nums">
          {resultCount} of {entries.length} releases
          {latest && <span> · latest v{latest.version}</span>}
        </p>
      </div>

      <div className="space-y-8 px-4 py-5 sm:px-6">
        {series.length === 0 && (
          <p className="py-10 text-center text-sm text-gray-500">No releases match this filter.</p>
        )}

        {series.map((s) => {
          const seriesOpen = !closedSeries.has(s.major);
          return (
            <section key={s.major}>
              <div className="mb-3 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleSeries(s.major)}
                  aria-expanded={seriesOpen}
                  className="flex items-center gap-1.5 text-gray-900 hover:text-blue-600"
                >
                  <Chevron open={seriesOpen} className="text-gray-400" />
                  <span className="text-base font-semibold tracking-tight">v{s.major}.x</span>
                </button>
                <span className="h-px flex-1 bg-gradient-to-r from-gray-200 to-transparent" aria-hidden="true" />
                <span className="text-xs text-gray-500 tabular-nums">
                  {s.total} release{s.total === 1 ? '' : 's'}
                  {s.counts.major > 0 && ` · ${s.counts.major} major`}
                  {s.counts.minor > 0 && ` · ${s.counts.minor} minor`}
                  {s.counts.patch > 0 && ` · ${s.counts.patch} patch${s.counts.patch === 1 ? '' : 'es'}`}
                </span>
              </div>

              {seriesOpen && (
                <div className="space-y-3">
                  {s.lines.map((line) => {
                    const head = line.head;
                    const isMajor = head?.level === 'major';
                    const headOpen = head ? openVersions.has(head.version) : false;
                    return (
                      <article
                        key={line.key}
                        className={`overflow-hidden rounded-xl bg-white ${
                          isMajor ? 'border border-violet-200 ring-1 ring-violet-100' : 'border border-gray-200 shadow-sm'
                        }`}
                      >
                        {head ? (
                          <>
                            <button
                              type="button"
                              onClick={() => toggleVersion(head.version)}
                              aria-expanded={headOpen}
                              className="flex w-full items-start gap-3 px-3 py-3 text-left hover:bg-gray-50/80 sm:px-4"
                            >
                              <span
                                className={`mt-px inline-flex shrink-0 items-center rounded-md px-2 py-1 font-mono text-xs font-semibold tabular-nums ${LEVEL_CHIP[head.level]}`}
                              >
                                v{head.version}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block text-sm font-semibold text-gray-900">{head.title}</span>
                                <span className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                                  <span className={`rounded px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${LEVEL_TAG[head.level]}`}>
                                    {head.level}
                                  </span>
                                  {line.patches.length > 0 && (
                                    <span>
                                      {line.patches.length} patch{line.patches.length === 1 ? '' : 'es'}
                                    </span>
                                  )}
                                  {latest?.version === head.version && (
                                    <span className="rounded bg-emerald-50 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                      Latest
                                    </span>
                                  )}
                                </span>
                              </span>
                              <Chevron open={headOpen} className="mt-1 text-gray-400" />
                            </button>
                            {headOpen && (
                              <div className="border-t border-gray-100 bg-gray-50/50 px-3 py-4 sm:px-4">
                                <ReleaseBody entry={head} />
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="px-3 py-2 text-xs font-medium text-gray-500 sm:px-4">{line.label}</div>
                        )}

                        {line.patches.length > 0 && (
                          <div className="relative border-t border-gray-100">
                            <span className="absolute left-6 top-0 h-full w-px -translate-x-1/2 bg-gray-200 sm:left-7" aria-hidden="true" />
                            {line.patches.map((patch) => {
                              const patchOpen = openVersions.has(patch.version);
                              return (
                                <div key={patch.version}>
                                  <button
                                    type="button"
                                    onClick={() => toggleVersion(patch.version)}
                                    aria-expanded={patchOpen}
                                    className="relative flex w-full items-start gap-2.5 py-2 pl-11 pr-3 text-left hover:bg-gray-50 sm:pl-12 sm:pr-4"
                                  >
                                    <span
                                      className="absolute left-6 top-3.5 h-2 w-2 -translate-x-1/2 rounded-full border-2 border-white bg-gray-300 sm:left-7"
                                      aria-hidden="true"
                                    />
                                    <span className="mt-px shrink-0 font-mono text-xs tabular-nums text-gray-500">v{patch.version}</span>
                                    <span className="min-w-0 flex-1 text-sm text-gray-700">
                                      {patch.title}
                                      {/* The newest release is not always a line head — when a
                                          patch ships on top of a minor, it is the current one. */}
                                      {latest?.version === patch.version && (
                                        <span className="ml-1.5 whitespace-nowrap rounded bg-emerald-50 px-1.5 py-px align-middle text-[10px] font-semibold uppercase tracking-wide text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                          Latest
                                        </span>
                                      )}
                                    </span>
                                    <Chevron open={patchOpen} className="mt-0.5 text-gray-300" />
                                  </button>
                                  {patchOpen && (
                                    <div className="border-y border-gray-100 bg-gray-50/50 py-3 pl-11 pr-3 sm:pl-12 sm:pr-4">
                                      <ReleaseBody entry={patch} />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
